import { Button, Form, Modal, Select, Space, Table, Tag, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CrownOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import {
  apiAddListZaloAccountUserPermissions,
  apiDeleteListZaloAccountUserPermissions,
  apiGetListUsers,
  apiGetListZaloAccountUserPermissions,
  apiGetListZaloAccounts,
  apiTransferPrimaryOwnership,
  apiUpdateListZaloAccountUserPermissions,
} from '../../api'

type Permission = {
  id: string
  zaloAccountId: number
  userId: number
  permissionLevel: 'VIEW' | 'SEND' | 'MANAGE'
  isPrimary: boolean
  status: 'ACTIVE' | 'INACTIVE'
  assignedBy: number
  assignedAt: string
}

type ZaloAccount = {
  id: number
  displayName: string
  zaloUid: string
  phone: string
  status: string
}

type CrmUser = {
  id: number
  full_name?: string
  username?: string
}

const LEVEL_OPTIONS = [
  { value: 'VIEW', label: 'VIEW — chỉ xem' },
  { value: 'SEND', label: 'SEND — xem + đăng nhập session' },
  { value: 'MANAGE', label: 'MANAGE — toàn quyền' },
]

function renderLevel(level: Permission['permissionLevel']) {
  if (level === 'MANAGE') return <Tag color="red">MANAGE</Tag>
  if (level === 'SEND') return <Tag color="blue">SEND</Tag>
  return <Tag>VIEW</Tag>
}

function renderStatus(status: Permission['status']) {
  if (status === 'ACTIVE') return <Tag color="green">ACTIVE</Tag>
  return <Tag color="orange">INACTIVE</Tag>
}

export default function ZaloPermissions() {
  const authState = useSelector(authSelector)
  const canAdd = authState?.account?.canAddListZaloAccountUserPermissions
  const canEditLevel = authState?.account?.canUpdateListZaloAccountUserPermissions
  const canRevoke = authState?.account?.canDeleteListZaloAccountUserPermissions
  const canTransfer = authState?.account?.canTransferPrimaryOwnership
  const [searchParams, setSearchParams] = useSearchParams()
  const [accounts, setAccounts] = useState<ZaloAccount[]>([])
  const [users, setUsers] = useState<CrmUser[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editModal, setEditModal] = useState<{ open: boolean; row: Permission | null }>({ open: false, row: null })
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()

  const userMap = useMemo(() => {
    const m = new Map<number, CrmUser>()
    users.forEach((u) => m.set(Number(u.id), u))
    return m
  }, [users])

  const fetchAccounts = useCallback(async () => {
    try {
      const res: any = await apiGetListZaloAccounts({ page: 1, size: 500 })
      setAccounts(Array.isArray(res?.data?.zaloAccounts) ? res.data.zaloAccounts : [])
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách Zalo account thất bại')
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const res: any = await apiGetListUsers({ limit: 500 })
      setUsers(Array.isArray(res?.data?.users) ? res.data.users : [])
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách CRM user thất bại')
    }
  }, [])

  const fetchPermissions = useCallback(async (accountId: number) => {
    setLoading(true)
    try {
      const res: any = await apiGetListZaloAccountUserPermissions({
        zaloAccountIds: [accountId],
        orderBy: '"isPrimary" desc, "createdAt" asc',
        page: 1,
        size: 200,
      })
      setPermissions(Array.isArray(res?.data?.zaloAccountUserPermissions) ? res.data.zaloAccountUserPermissions : [])
    } catch (err: any) {
      message.error(err?.message || 'Tải danh sách phân quyền thất bại')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAccounts()
    fetchUsers()
  }, [fetchAccounts, fetchUsers])

  useEffect(() => {
    const fromQuery = searchParams.get('accountId')
    const parsed = fromQuery ? Number(fromQuery) : NaN
    if (Number.isFinite(parsed) && parsed > 0 && parsed !== selectedAccountId) {
      setSelectedAccountId(parsed)
    }
  }, [searchParams, selectedAccountId])

  useEffect(() => {
    if (selectedAccountId !== null) {
      fetchPermissions(selectedAccountId)
    }
  }, [selectedAccountId, fetchPermissions])

  const handleAccountChange = (next: number | null) => {
    setSelectedAccountId(next)
    if (next === null) {
      searchParams.delete('accountId')
    } else {
      searchParams.set('accountId', String(next))
    }
    setSearchParams(searchParams, { replace: true })
  }

  const handleAdd = async () => {
    try {
      const values = await form.validateFields()
      if (selectedAccountId === null) {
        message.warning('Vui lòng chọn Zalo account trước.')
        return
      }
      const res: any = await apiAddListZaloAccountUserPermissions({
        zaloAccountUserPermissions: [
          {
            zaloAccountId: selectedAccountId,
            userId: values.userId,
            permissionLevel: values.permissionLevel,
            isPrimary: false,
            status: 'ACTIVE',
          },
        ],
      })
      // Core swallows duplicate-key errors with a silent skip — empty result
      // means the row already exists for (zaloAccountId, userId).
      const granted = Array.isArray(res?.data?.zaloAccountUserPermissions)
        ? res.data.zaloAccountUserPermissions
        : []
      if (granted.length === 0) {
        message.error('User này đã có quyền trên Zalo account. Hãy đổi level thay vì cấp mới.')
        return
      }
      message.success('Đã cấp quyền.')
      setModalOpen(false)
      form.resetFields()
      fetchPermissions(selectedAccountId)
    } catch (err: any) {
      message.error(err?.message || 'Cấp quyền thất bại')
    }
  }

  const handleRevoke = async (id: string, isPrimary: boolean) => {
    if (isPrimary) {
      message.warning('Không thể thu hồi primary owner. Hãy chuyển primary sang user khác trước.')
      return
    }
    Modal.confirm({
      title: 'Thu hồi quyền',
      content: 'Xác nhận thu hồi quyền truy cập của user này?',
      onOk: async () => {
        try {
          await apiDeleteListZaloAccountUserPermissions({ ids: [id] })
          message.success('Đã thu hồi.')
          if (selectedAccountId !== null) fetchPermissions(selectedAccountId)
        } catch (err: any) {
          message.error(err?.message || 'Thu hồi thất bại')
        }
      },
    })
  }

  const handleSetPrimary = (newPrimary: Permission) => {
    if (newPrimary.isPrimary) return
    if (newPrimary.status !== 'ACTIVE') {
      message.warning('Chỉ chuyển primary cho permission đang ACTIVE.')
      return
    }
    const currentPrimary = permissions.find((p) => p.isPrimary)
    Modal.confirm({
      title: 'Chuyển primary ownership',
      content: currentPrimary
        ? `Chuyển primary từ user #${currentPrimary.userId} sang user #${newPrimary.userId}?`
        : `Đặt user #${newPrimary.userId} làm primary owner?`,
      okText: 'Chuyển',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await apiTransferPrimaryOwnership({
            zaloAccountId: newPrimary.zaloAccountId,
            toUserId: newPrimary.userId,
          })
          message.success('Đã chuyển primary.')
          if (selectedAccountId !== null) fetchPermissions(selectedAccountId)
        } catch (err: any) {
          message.error(err?.message || 'Chuyển primary thất bại')
        }
      },
    })
  }

  const openEditLevel = (row: Permission) => {
    editForm.setFieldsValue({ permissionLevel: row.permissionLevel })
    setEditModal({ open: true, row })
  }

  const handleEditLevel = async () => {
    if (!editModal.row) return
    try {
      const values = await editForm.validateFields()
      if (values.permissionLevel === editModal.row.permissionLevel) {
        setEditModal({ open: false, row: null })
        return
      }
      await apiUpdateListZaloAccountUserPermissions({
        zaloAccountUserPermissions: [
          { id: editModal.row.id, permissionLevel: values.permissionLevel },
        ],
      })
      message.success('Đã đổi quyền.')
      setEditModal({ open: false, row: null })
      editForm.resetFields()
      if (selectedAccountId !== null) fetchPermissions(selectedAccountId)
    } catch (err: any) {
      message.error(err?.message || 'Đổi quyền thất bại')
    }
  }

  const columns = [
    {
      title: 'User',
      dataIndex: 'userId',
      render: (userId: number) => {
        const u = userMap.get(Number(userId))
        return u ? `${u.full_name || u.username} (#${userId})` : `#${userId}`
      },
    },
    { title: 'Level', dataIndex: 'permissionLevel', render: renderLevel },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      render: (v: boolean) => (v ? <Tag color="gold">PRIMARY</Tag> : '—'),
    },
    { title: 'Status', dataIndex: 'status', render: renderStatus },
    {
      title: 'Assigned by',
      dataIndex: 'assignedBy',
      render: (id: number) => {
        const u = userMap.get(Number(id))
        return u ? u.full_name || u.username : `#${id}`
      },
    },
    {
      title: 'Thao tác',
      render: (_: any, row: Permission) => (
        <Space size="small">
          {canTransfer && (
            <Button
              icon={<CrownOutlined />}
              size="small"
              disabled={row.isPrimary || row.status !== 'ACTIVE'}
              onClick={() => handleSetPrimary(row)}
            >
              Đặt primary
            </Button>
          )}
          {canEditLevel && (
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditLevel(row)}
            >
              Đổi level
            </Button>
          )}
          {canRevoke && (
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              disabled={row.isPrimary}
              onClick={() => handleRevoke(row.id, row.isPrimary)}
            >
              Thu hồi
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>Phân quyền Zalo Account</h2>
      <Space style={{ marginBottom: 16 }}>
        <Select
          showSearch
          placeholder="Chọn Zalo account..."
          style={{ minWidth: 360 }}
          value={selectedAccountId ?? undefined}
          onChange={handleAccountChange}
          options={accounts.map((a) => ({
            value: a.id,
            label: `${a.displayName || '(no name)'}${a.phone ? ` — ${a.phone}` : ''} (#${a.id})`,
          }))}
          filterOption={(input, option) =>
            String(option?.label || '').toLowerCase().includes(input.toLowerCase())
          }
        />
        {canAdd && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={selectedAccountId === null}
            onClick={() => setModalOpen(true)}
          >
            Cấp quyền cho user
          </Button>
        )}
      </Space>

      <Table
        columns={columns as any}
        dataSource={permissions}
        loading={loading}
        rowKey="id"
        pagination={false}
      />

      <Modal
        title="Cấp quyền truy cập"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false)
          form.resetFields()
        }}
        onOk={handleAdd}
        okText="Cấp quyền"
        cancelText="Huỷ"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="CRM User"
            name="userId"
            rules={[{ required: true, message: 'Chọn user' }]}
          >
            <Select
              showSearch
              placeholder="Chọn user..."
              options={users.map((u) => ({
                value: Number(u.id),
                label: `${u.full_name || u.username} (#${u.id})`,
              }))}
              filterOption={(input, option) =>
                String(option?.label || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            label="Quyền"
            name="permissionLevel"
            rules={[{ required: true, message: 'Chọn level' }]}
            initialValue="SEND"
          >
            <Select options={LEVEL_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Đổi quyền"
        open={editModal.open}
        onCancel={() => {
          setEditModal({ open: false, row: null })
          editForm.resetFields()
        }}
        onOk={handleEditLevel}
        okText="Lưu"
        cancelText="Huỷ"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="User">
            <span>
              {editModal.row
                ? `${userMap.get(Number(editModal.row.userId))?.full_name
                    || userMap.get(Number(editModal.row.userId))?.username
                    || '#' + editModal.row.userId}`
                : ''}
            </span>
          </Form.Item>
          <Form.Item
            label="Quyền mới"
            name="permissionLevel"
            rules={[{ required: true, message: 'Chọn level' }]}
          >
            <Select options={LEVEL_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
